/**
 * ส่งสลิปทางอีเมล — เลือกผู้รับก่อนส่ง: คนเดียว · บางคน · ทั้งหมด · ตามแผนก (พี่ปุ้ย 28 ก.ย. 2569)
 * ส่งได้เฉพาะงวดที่ปิดแล้ว · ส่งเป็นชุดละ batch_max คน (สร้าง PDF ใช้เวลา) พร้อมแถบความคืบหน้า + ผลรายคน
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, ChevronLeft, Search, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { apiFetch } from '../../lib/api-client';
import { useConfirm } from '../../components/ui/confirm';
import { useToast } from '../../components/ui/toast';

interface Recipient {
  emp_code: string; name: string; nickname: string | null; department: string | null; position: string | null;
  email: string | null; net: number; can_send: boolean; reason: string | null;
}
interface Template { subject: string; body: string; default_subject: string; default_body: string }
interface RecipientsRes { period: string; from: string; company: string; batch_max: number; closed: boolean; rows: Recipient[]; template: Template }
interface SendResult { emp_code: string; ok: boolean; email?: string; reason?: string }

const fill = (t: string, v: { name: string; period: string; company: string }) =>
  t.replaceAll('{ชื่อ}', v.name).replaceAll('{งวด}', v.period).replaceAll('{บริษัท}', v.company);

const baht = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function PayrollEmailSlips() {
  const [params] = useSearchParams();
  const period = params.get('period') ?? '';
  const confirm = useConfirm();
  const toast = useToast();
  const [data, setData] = useState<RecipientsRes | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('');
  const [pos, setPos] = useState(''); // fb#268: TN กรองตามตำแหน่ง (ทีม) เป็นหลัก
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [results, setResults] = useState<SendResult[]>([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [saveTpl, setSaveTpl] = useState(true);

  useEffect(() => {
    if (!period) return;
    setData(null); setLoadErr(null);
    apiFetch<RecipientsRes>(`/owner/payroll/email-slips/recipients?period=${period}`)
      .then((d) => { setData(d); setSubject(d.template.subject); setMessage(d.template.body); })
      .catch((e) => setLoadErr(e instanceof Error ? e.message : 'โหลดรายชื่อไม่สำเร็จ'));
  }, [period]);

  const rows = data?.rows ?? [];
  const depts = useMemo(() => [...new Set(rows.map((r) => r.department ?? '').filter(Boolean))].sort(), [rows]);
  const positions = useMemo(() => [...new Set(rows.map((r) => r.position ?? '').filter(Boolean))].sort((a, b) => a.localeCompare(b, 'th')), [rows]);
  const shown = useMemo(() => rows.filter((r) => {
    if (dept && (r.department ?? '') !== dept) return false;
    if (pos && (r.position ?? '') !== pos) return false;
    if (!q.trim()) return true;
    const k = q.trim().toLowerCase();
    return [r.emp_code, r.name, r.nickname ?? '', r.email ?? '', r.position ?? ''].some((v) => v.toLowerCase().includes(k));
  }), [rows, dept, pos, q]);
  const sendableShown = shown.filter((r) => r.can_send);
  const pickedRows = rows.filter((r) => picked.has(r.emp_code));
  const cannot = rows.filter((r) => !r.can_send);

  function toggle(code: string) {
    setPicked((s) => { const n = new Set(s); if (n.has(code)) n.delete(code); else n.add(code); return n; });
  }
  function pickShown(on: boolean) {
    setPicked((s) => { const n = new Set(s); for (const r of sendableShown) { if (on) n.add(r.emp_code); else n.delete(r.emp_code); } return n; });
  }

  async function send(codes: string[]) {
    if (!data || codes.length === 0) return;
    if (!subject.trim() || !message.trim()) { toast.error('กรอกหัวเรื่องและข้อความอีเมลก่อนส่ง'); return; }
    const ok = await confirm({
      title: `ส่งสลิปทางอีเมล ${codes.length} คน?`,
      message: `งวด ${period} · ส่งจาก ${data.from} · แนบไฟล์ PDF ไปที่อีเมลในประวัติของแต่ละคน${codes.length <= 3 ? ` (${pickedRows.filter((r) => codes.includes(r.emp_code)).map((r) => `${r.name} → ${r.email}`).join(' · ')})` : ''} · ส่งแล้วเรียกคืนไม่ได้`,
      confirmText: `ส่ง ${codes.length} ฉบับ`,
    });
    if (!ok) return;
    setSending(true);
    setProgress({ done: 0, total: codes.length });
    const all: SendResult[] = [];
    try {
      for (let i = 0; i < codes.length; i += data.batch_max) {
        const chunk = codes.slice(i, i + data.batch_max);
        try {
          const r = await apiFetch<{ results: SendResult[] }>('/owner/payroll/email-slips', {
            method: 'POST',
            // จำข้อความส่งไปกับชุดแรกชุดเดียว
            body: { period, emp_codes: chunk, subject, message, save_template: saveTpl && i === 0 },
          });
          all.push(...r.results);
        } catch (e) {
          // ชุดนี้ล้มทั้งชุด — บันทึกเป็นไม่สำเร็จรายคน ส่งชุดถัดไปต่อ (ส่งซ้ำเฉพาะคนที่ล้มได้)
          const msg = e instanceof Error ? e.message : 'ส่งไม่สำเร็จ';
          // ชุดแรกที่บันทึกข้อความด้วยล้ม = ไม่รู้ว่าข้อความถูกบันทึกไหม → หยุดทั้งหมด ไม่ส่งคนที่เหลือ (astra)
          if (i === 0 && saveTpl) {
            all.push(...codes.map((c) => ({ emp_code: c, ok: false, reason: `${msg} — หยุดส่งทั้งหมด` })));
            setProgress({ done: codes.length, total: codes.length });
            setResults([...all]);
            break;
          }
          all.push(...chunk.map((c) => ({ emp_code: c, ok: false, reason: msg })));
        }
        setProgress({ done: Math.min(i + chunk.length, codes.length), total: codes.length });
        setResults([...all]);
      }
    } finally {
      setSending(false);
    }
    const okN = all.filter((r) => r.ok).length;
    const bad = all.length - okN;
    if (bad === 0) toast.success(`ส่งสลิปทางอีเมลแล้ว ${okN} คน`);
    else toast.error(`ส่งสำเร็จ ${okN} คน · ไม่สำเร็จ ${bad} คน — ดูรายชื่อด้านล่างแล้วกดส่งใหม่เฉพาะคนที่ไม่สำเร็จ`);
    setPicked(new Set(all.filter((r) => !r.ok).map((r) => r.emp_code).filter((c) => rows.find((x) => x.emp_code === c)?.can_send)));
  }

  const nameOf = (code: string) => rows.find((r) => r.emp_code === code)?.name ?? code;
  const failed = results.filter((r) => !r.ok);

  return (
    <AdminLayout title="ส่งสลิปทางอีเมล">
      <div className="max-w-4xl mx-auto space-y-4">
        <Link to="/owner/cycle#payroll" className="inline-flex items-center gap-1 text-sm text-brand-600"><ChevronLeft className="w-4 h-4" /> กลับหน้าทำเงินเดือน</Link>
        <div className="card p-4">
          <div className="flex items-center gap-2 font-semibold"><Mail className="w-5 h-5 text-brand-600" /> ส่งสลิปทางอีเมล · งวด {period || '—'}</div>
          <p className="text-xs text-gray-500 mt-1">เลือกคนที่จะส่ง (คนเดียว · หลายคน · ทั้งแผนก · ทั้งหมด) แล้วกดส่ง · แนบสลิป PDF ไปที่อีเมลในประวัติพนักงาน{data ? ` · ผู้ส่ง ${data.from}` : ''}</p>
        </div>

        {!period && <div className="card p-4 text-sm text-gray-500">ไม่ได้ระบุงวด — กลับไปเลือกงวดที่หน้าทำเงินเดือน</div>}
        {loadErr && <div className="card p-4 text-sm text-red-700 flex items-center gap-2"><XCircle className="w-4 h-4" /> {loadErr}</div>}
        {period && !data && !loadErr && (
          <div className="card p-4 space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}</div>
        )}

        {data && !data.closed && (
          <div className="card p-4 text-sm text-amber-800 bg-amber-50 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>งวด {period} ยังไม่ปิด — ส่งสลิปได้หลังกดปิดงวด (ล็อก) ที่หน้าทำเงินเดือน ตัวเลขในร่างยังเปลี่ยนได้</span>
          </div>
        )}

        {data && data.closed && (
          <>
            <div className="card p-3 flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                <input className="input pl-8 w-full" placeholder="ค้นหาชื่อ · ชื่อเล่น · รหัส · อีเมล" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <select className="input w-auto" value={dept} onChange={(e) => setDept(e.target.value)}>
                <option value="">ทุกแผนก ({rows.length})</option>
                {depts.map((d) => <option key={d} value={d}>{d} ({rows.filter((r) => r.department === d).length})</option>)}
              </select>
              <select className="input w-auto" value={pos} onChange={(e) => setPos(e.target.value)} aria-label="ตำแหน่ง">
                <option value="">ทุกตำแหน่ง ({rows.length})</option>
                {positions.map((p) => <option key={p} value={p}>{p} ({rows.filter((r) => r.position === p).length})</option>)}
              </select>
              <button className="btn-secondary text-sm" onClick={() => pickShown(true)} disabled={sendableShown.length === 0}>เลือกที่แสดงทั้งหมด ({sendableShown.length})</button>
              <button className="btn-secondary text-sm" onClick={() => pickShown(false)}>ไม่เลือกที่แสดง</button>
              {picked.size > 0 && <button className="text-sm text-gray-500 underline" onClick={() => setPicked(new Set())}>ล้างที่เลือก</button>}
            </div>

            {cannot.length > 0 && (
              <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> ส่งไม่ได้ {cannot.length} คน (ไม่มีอีเมล/อีเมลผิดรูปแบบ) — เติมอีเมลที่ ข้อมูลพนักงาน แล้วกลับมาหน้านี้</p>
            )}

            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-left">พนักงาน</th>
                    <th className="p-2 text-left">แผนก / ตำแหน่ง</th>
                    <th className="p-2 text-left">อีเมล</th>
                    <th className="p-2 text-right">สุทธิ</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.emp_code} className={`border-t ${r.can_send ? 'cursor-pointer hover:bg-gray-50' : 'opacity-60'}`} onClick={() => r.can_send && toggle(r.emp_code)}>
                      <td className="p-2 text-center">
                        <input type="checkbox" checked={picked.has(r.emp_code)} disabled={!r.can_send} onChange={() => toggle(r.emp_code)} onClick={(e) => e.stopPropagation()} aria-label={`เลือก ${r.name}`} />
                      </td>
                      <td className="p-2"><div className="font-medium">{r.name}{r.nickname ? ` (${r.nickname})` : ''}</div><div className="text-[11px] text-gray-400">{r.emp_code}</div></td>
                      <td className="p-2 text-xs text-gray-600">{r.department ?? '—'}<br />{r.position ?? ''}</td>
                      <td className="p-2 text-xs">{r.email ?? <span className="text-amber-700">{r.reason}</span>}{r.email && !r.can_send && <div className="text-amber-700">{r.reason}</div>}</td>
                      <td className="p-2 text-right whitespace-nowrap">{baht(r.net)}</td>
                    </tr>
                  ))}
                  {shown.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-gray-400">ไม่พบพนักงานตามที่ค้นหา</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="card p-3 space-y-2">
              <div className="text-sm font-semibold">ข้อความอีเมล</div>
              <div>
                <label className="label">หัวเรื่อง</label>
                <input className="input w-full" value={subject} maxLength={150} onChange={(e) => setSubject(e.target.value)} />
              </div>
              <div>
                <label className="label">ข้อความ</label>
                <textarea className="input w-full min-h-[160px]" value={message} maxLength={3000} onChange={(e) => setMessage(e.target.value)} />
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span>ใส่แทนอัตโนมัติ:</span>
                {['{ชื่อ}', '{งวด}', '{บริษัท}'].map((t) => (
                  <button key={t} type="button" className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50" onClick={() => setMessage((m) => `${m}${t}`)}>{t}</button>
                ))}
                <button type="button" className="underline ml-auto" onClick={() => { setSubject(data.template.default_subject); setMessage(data.template.default_body); }}>ใช้ข้อความเริ่มต้น</button>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={saveTpl} onChange={(e) => setSaveTpl(e.target.checked)} /> จำข้อความนี้ไว้ใช้ครั้งหน้า</label>
              {(() => {
                const ex = pickedRows[0] ?? rows.find((r) => r.can_send);
                if (!ex) return null;
                const v = { name: ex.name, period, company: data.company };
                return (
                  <div className="rounded border border-gray-200 bg-gray-50 p-2 text-xs">
                    <div className="text-gray-500 mb-1">ตัวอย่างที่ {ex.name} จะได้รับ · แนบไฟล์ slip-{period}-{ex.emp_code}.pdf</div>
                    <div className="font-semibold">{fill(subject, v)}</div>
                    <div className="whitespace-pre-wrap mt-1">{fill(message, v)}</div>
                  </div>
                );
              })()}
            </div>

            {progress && (
              <div className="card p-3">
                <div className="text-sm mb-1">{sending ? 'กำลังส่ง…' : 'ส่งเสร็จแล้ว'} {progress.done}/{progress.total}</div>
                <div className="h-2 bg-gray-100 rounded"><div className="h-2 bg-brand-600 rounded" style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }} /></div>
              </div>
            )}

            {results.length > 0 && (
              <div className="card p-3 text-sm space-y-1">
                <div className="flex items-center gap-2 text-green-700"><CheckCircle2 className="w-4 h-4" /> สำเร็จ {results.length - failed.length} คน</div>
                {failed.length > 0 && (
                  <div className="text-red-700">
                    <div className="flex items-center gap-2"><XCircle className="w-4 h-4" /> ไม่สำเร็จ {failed.length} คน (เลือกไว้ให้แล้ว กดส่งใหม่ได้)</div>
                    <ul className="ml-6 list-disc text-xs">{failed.map((f) => <li key={f.emp_code}>{nameOf(f.emp_code)} — {f.reason}</li>)}</ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 sticky bottom-0 bg-white/90 py-3">
              <Link to="/owner/cycle#payroll" className="btn-secondary">ยกเลิก</Link>
              <button className="btn-cta" disabled={sending || picked.size === 0} onClick={() => send([...picked])}>
                <Mail className="w-4 h-4" /> {sending ? 'กำลังส่ง…' : `ส่งอีเมล ${picked.size} คน`}
              </button>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
